import { NotificationBadge } from '@monash/portal-frontend-common';
import React, { useContext, useRef } from 'react';
import c from './nav.module.scss';
import {
  Icon,
  useResponsiveValue,
  Logo,
  IconButton,
  RouterState,
  themes,
  isColorCloserToBlack,
} from '@monash/portal-react';
import { PageContext } from '@/components/providers/page-provider/PageProvider';
import PageNav from './page-nav/PageNav';
import MobilePageNav from './mobile-page-nav/MobilePageNav';
import { SCROLL_DIRECTION } from '@/constants/scroll-direction';
import useScrollDetection from '@/hooks/use-scroll-detection';
import { DELAY_TYPE } from '@/constants/delay-type';
import { SCROLL_POSITION } from '@/constants/scroll-position';
import classNames from 'classnames';
import { ThemeContext } from '@/components/providers/theme-provider/ThemeProvider';
import TopNavItems from './top-nav-items/TopNavItems';

export const MOBILE_RESPONSIVE = [
  {
    mq: '(max-width: 359px)',
    value: 'XS',
  },
  {
    mq: '(min-width: 360px) and (max-width: 699px)',
    value: 'S',
  },
  {
    mq: '(min-width: 700px)',
    value: 'L',
  },
];

const Nav = ({
  setSidebarOpen,
  sidebarTriggerRef,
  showThemer,
  setShowThemer,
}) => {
  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);
  const isMobile = responsiveSize === 'S' || responsiveSize === 'XS';

  const scrollElemSelectorStr = '#VerticalMain';

  const { direction, position } = useScrollDetection({
    selectorStr: scrollElemSelectorStr,
    delayType: DELAY_TYPE.THROTTLE,
    delay: 200,
    threshold: 10,
    detectUserScrollOnly: true,
  });

  const condensed =
    (direction === SCROLL_DIRECTION.DOWN && position !== SCROLL_POSITION.TOP) ||
    position === SCROLL_POSITION.BOTTOM;

  // Search
  const { route } = useContext(RouterState);

  // AX - set up trigger ref
  const { backgroundStyle } = useContext(PageContext);

  // ref for pageNav responsive behaviours
  const navMiddleRef = useRef();

  // cache condition for reuse
  const shouldHideTopNav = isMobile && condensed;

  // Get canvas text color
  const { selectedTheme } = useContext(ThemeContext);

  const shouldUseDefaultLogo = isColorCloserToBlack(
    themes[selectedTheme].variables['--canvas-text-color']
  );

  // Get the Logo variant
  const getLogo = () => {
    if (isMobile) {
      return shouldUseDefaultLogo ? <Logo.Shield /> : <Logo.ShieldReverse />;
    } else {
      return shouldUseDefaultLogo ? (
        <Logo.MonashStacked />
      ) : (
        <Logo.MonashStackedReverse />
      );
    }
  };

  return (
    <div
      className={classNames(c.navContainer, {
        [c.hideTopNav]: shouldHideTopNav,
      })}
      id="topNavContainer"
      role="banner"
    >
      <div className={c.backgroundContainer}>
        <div
          className={c.background}
          style={
            isMobile
              ? { ...backgroundStyle, width: '100vw', transform: 'unset' }
              : backgroundStyle
          }
        />
      </div>

      <div className={c.nav} id="topNav">
        <div className={c.left}>
          {responsiveSize === 'XS' && (
            <IconButton
              icon={Icon.Burger}
              mode="canvas"
              onClick={() =>
                setSidebarOpen((prevSidebarOpen) => !prevSidebarOpen)
              }
              ref={sidebarTriggerRef}
            />
          )}

          <a
            href="/"
            aria-label="home"
            data-tracking-event="topnav-home-logo"
            className={c.logo}
          >
            {getLogo()}
          </a>
        </div>
        {!isMobile && (
          <div className={c.middle} ref={navMiddleRef}>
            <PageNav navMiddleRef={navMiddleRef} />
          </div>
        )}

        <div className={c.right}>
          <div className={c.navButton}>
            <NotificationBadge skipLiveMessages badgeRole="none" />
            {responsiveSize !== 'XS' && (
              <TopNavItems
                showThemer={showThemer}
                setShowThemer={setShowThemer}
              />
            )}
          </div>
        </div>
      </div>

      {isMobile && !route.path.startsWith('/search') && (
        <div className={c.middle}>
          <MobilePageNav />
        </div>
      )}
    </div>
  );
};

export default Nav;
